import { ExternalLink, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Separator } from '@/components/ui/separator';
import { mediaUrl } from '@/core/api';
import { formatBytes } from '@/lib/format';
import { youtubeEmbedUrl } from '../lib/youtube';
import type { AssetBlockData, LessonBlock } from '../types';
import { RICH_TEXT_PROSE_CLASS } from '../lib/prose';

function Caption({ text }: { text?: string }) {
  if (!text) return null;
  return <figcaption className="text-muted-foreground mt-1.5 text-xs">{text}</figcaption>;
}

function MissingAsset() {
  const { t } = useTranslation(['curriculum']);
  return (
    <p className="text-muted-foreground rounded-md border border-dashed px-3 py-2 text-sm italic">
      {t('curriculum:view.assetUnavailable')}
    </p>
  );
}

function AssetBlockView({
  kind,
  data,
}: {
  kind: 'image' | 'video' | 'audio' | 'file';
  data: AssetBlockData;
}) {
  const { t, i18n } = useTranslation(['curriculum']);
  // Read payloads enrich asset blocks with `url` (curriculum/blocks.py
  // build_read_blocks); a missing/deleted asset arrives without one.
  if (!data.url) return <MissingAsset />;
  const src = mediaUrl(data.url);

  if (kind === 'image') {
    return (
      <figure>
        <img
          src={src}
          alt={data.caption || data.filename || ''}
          loading="lazy"
          className="max-w-full rounded-md border"
        />
        <Caption text={data.caption} />
      </figure>
    );
  }
  if (kind === 'video') {
    return (
      <figure>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption -- teacher uploads carry no caption tracks; the optional text caption renders below */}
        <video controls preload="metadata" src={src} className="w-full rounded-md border" />
        <Caption text={data.caption} />
      </figure>
    );
  }
  if (kind === 'audio') {
    return (
      <figure>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption -- teacher uploads carry no caption tracks; the optional text caption renders below */}
        <audio controls preload="metadata" src={src} className="w-full" />
        <Caption text={data.caption} />
      </figure>
    );
  }
  return (
    <a
      href={src}
      download={data.filename}
      className="bg-muted/60 hover:bg-muted flex items-center gap-2 rounded-md border px-3 py-2 text-sm"
    >
      <FileText className="text-muted-foreground size-4 shrink-0" />
      <span className="truncate font-medium">{data.label || data.filename}</span>
      {typeof data.size === 'number' ? (
        <span className="text-muted-foreground shrink-0">
          {formatBytes(data.size, i18n.language)}
        </span>
      ) : null}
      <span className="sr-only">{t('curriculum:view.download')}</span>
    </a>
  );
}

function BlockView({ block }: { block: LessonBlock }) {
  const { t } = useTranslation(['curriculum']);
  switch (block.type) {
    case 'rich_text':
      return (
        <div
          className={RICH_TEXT_PROSE_CLASS}
          // SAFE ONLY because the server sanitizes rich_text HTML on write
          // with a strict nh3 allowlist (curriculum/blocks.py sanitize_html;
          // FE-ADR-011) — the client never renders unsanitized user HTML.
          dangerouslySetInnerHTML={{ __html: block.data.html }}
        />
      );
    case 'image':
    case 'video':
    case 'audio':
    case 'file':
      return <AssetBlockView kind={block.type} data={block.data} />;
    case 'youtube':
      return (
        <figure>
          {/* Privacy-enhanced host, browser-lazy loading (FE-ADR-011). */}
          <iframe
            src={youtubeEmbedUrl(block.data.video_id)}
            title={block.data.caption || t('curriculum:view.youtubeTitle')}
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="aspect-video w-full rounded-md border"
          />
          <Caption text={block.data.caption} />
        </figure>
      );
    case 'link':
      return (
        <a
          href={block.data.url}
          target="_blank"
          rel="noreferrer"
          className="text-primary inline-flex items-center gap-1.5 text-sm underline underline-offset-4"
        >
          <ExternalLink className="size-4 shrink-0" />
          {block.data.label || block.data.url}
        </a>
      );
    case 'divider':
      return <Separator className="my-2" />;
  }
}

/** Renders a lesson's (server-sanitized/enriched) block document — shared by
 *  every reader role; pupils receive only published, visible lessons. */
export function LessonBlocksView({ blocks }: { blocks: LessonBlock[] }) {
  const { t } = useTranslation(['curriculum']);
  if (blocks.length === 0) {
    return <p className="text-muted-foreground text-sm italic">{t('curriculum:view.empty')}</p>;
  }
  return (
    <div className="space-y-4">
      {blocks.map(block => (
        <BlockView key={block.id} block={block} />
      ))}
    </div>
  );
}
